import React, { useState, useEffect, useRef } from 'react';
import { Badge, Tooltip } from 'antd';
import scss from './CrashSightHeader.scss';
import RestHelper from 'utils/RestHelper';
import { connect, useSelector } from 'react-redux';
import { isZh, ze } from 'utils/zhEn';
import moment from 'moment';
import NotificationDrawer from './NoticeDrawer.jsx'
import PropTypes from "prop-types";
import reportEvent, { EVENT_ACTIONS } from 'utils/reportEvent.js';
import NoticeIconNormal from 'svg/v2/newcs_dashboard_topbar_notice_icon_normal.svg';
import { isIos } from 'utils/platform';
import { ServerAppSettings } from 'utils/server-app-settings';
import { NoticeRecordCategory } from 'utils/constants/notice-record-category';
import useZuStore from 'store/zustand/zustand-store';

// 提取数据过滤函数，用于筛选出符合当前语言的有效数据
const filterPresentData = (rspData) => {
  return rspData.filter(item => isZh()? item.hasZh : item.hasEn);
};

// 提取数据格式化函数，用于给数据添加额外属性等格式化操作
const formatNoticeData = (presentData) => {
  return (presentData || []).map(item => ({
     ...item,
      showEdit: false
  }));
};

const NoticeButton = (prop) => {
  const { reduxState, appId, pid } = prop;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [ listNotices, setListNotices ] = useState([]);
  const [ total, setTotal ] = useState(0);
  const [ badgeCount, setBadgeCount ] = useState(0);
  const isSuper = !!reduxState.user.get('current').get('isSuper');
  // 告警历史里"自定义告警 - OOM"类型是否可见：仅 iOS 项目 + 开启 enableAlarmOomCategory 开关（王者需求）
  const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
  const showOomNoticeType = !!serverAppSettings[ServerAppSettings.keys.enableAlarmOomCategory] && isIos(pid);
  const openPushFailedTrigger = useZuStore((state) => state.noticeDrawer.openPushFailedTrigger);
  const lastOpenTriggerRef = useRef(openPushFailedTrigger);

  // 独立查询当前项目是否存在未读的「告警推送失败」消息，避免被前 10 条普通崩溃告警挤出第一页导致顶部横幅漏显
  const checkUnreadAlarmPushFailed = async (targetAppId = appId) => {
    if (!targetAppId) {
      useZuStore.getState().noticeDrawer.setUnreadAlarmPushFailedCount(0);
      return;
    }
    try {
      const rsp = await RestHelper.mustPost('/redir/api/notice/listNotices', {
        appId: targetAppId,
        lang: isZh() ? 'zh' : 'en',
        pageNumber: 1,
        pageSize: 1,
        startTime: moment().subtract(1, 'month').format('YYYY-MM-DD 00:00:00'),
        endTime: moment().add(1, 'days').format('YYYY-MM-DD 00:00:00'),
        onlyUnread: true,
        hierarchyLevel: 'APP',
        categoryList: [NoticeRecordCategory.APP_CUSTOM_ALARM_PUSH_FAILED],
      });
      const unreadFailedTotal = rsp?.json?.data?.total || 0;
      useZuStore.getState().noticeDrawer.setUnreadAlarmPushFailedCount(unreadFailedTotal);
    } catch (e) {
      console.error('checkUnreadAlarmPushFailed failed', e);
    }
  };

  useEffect(() => {
    useZuStore.getState().noticeDrawer.setUnreadAlarmPushFailedCount(0);
    if (appId) {
      checkUnreadAlarmPushFailed(appId);
    }
  }, [appId]);

  useEffect(() => {
    if (openPushFailedTrigger && openPushFailedTrigger !== lastOpenTriggerRef.current) {
      lastOpenTriggerRef.current = openPushFailedTrigger;
      setDrawerOpen(true);
      reportEvent({
        action: EVENT_ACTIONS.EXPOSURE,
        tp1: '消息中心曝光',
      });
    }
  }, [openPushFailedTrigger]);

  //获取公告数据
  const getListNotices = async({current,pageSize,onlyUnread,complexContentFilter} = {}) => {
    setListNotices([]);
    const rsp = await RestHelper.mustPost('/redir/api/notice/listNotices',{
      appId,
      lang: isZh() ? 'zh' : 'en',
      pageNumber: current,
      pageSize: pageSize,
      startTime: moment().subtract(1, 'month').format('YYYY-MM-DD 00:00:00'),
      endTime: moment().add(1, 'days').format('YYYY-MM-DD 00:00:00'),
      onlyUnread: onlyUnread,
      ...complexContentFilter,
    })
    const rspData = rsp.json.data.items;
    const total = rsp.json.data.total;
    const presentData = filterPresentData(rspData);
    const data = formatNoticeData(presentData);
    if(onlyUnread && !complexContentFilter){
      setBadgeCount(total)
    }
    setListNotices(data);
    setTotal(total);
    checkUnreadAlarmPushFailed(appId);
  }

  function onChangeDrawerOpen(v) {
    setDrawerOpen(v);
  }

  //用户一键已读所有通知
  const getMarkAllAsRead = async() => {
    await RestHelper.mustPost('/redir/api/notice/markAllAsRead',{
      appId, //把指定项目下的通知全部标为已读
    })
    await checkUnreadAlarmPushFailed(appId);
  }

  //用户修改通知的已读状态
  const getChangeReadStatus = async(recordId,hasRead) => {
    const rsp = await RestHelper.mustPost('/redir/api/notice/changeReadStatus',{
      appId,
      recordId,
      hasRead: !hasRead, //true表示标为已读false表示标为未读
    })
    await checkUnreadAlarmPushFailed(appId);
  }

  //打开抽屉弹框
  const drawerShow = () => {
    setDrawerOpen(true);
    // 消息中心曝光
    reportEvent({
      action: EVENT_ACTIONS.EXPOSURE,
      tp1: '消息中心曝光',
    });
  };

  const drawerDisable = () => {
    setDrawerOpen(false);
  }

  return (<div style={{cursor: 'pointer', marginTop:'6px'}}>
    <Badge size="small" count={badgeCount} offset={[5, 0]} onClick={drawerShow} color='#EE2F27'>
      <div className={scss.noticeIcon}>
        <Tooltip title={ze('消息提示','Message')} placement='bottom'><NoticeIconNormal /></Tooltip>
      </div>
    </Badge>
    <NotificationDrawer
      appId={appId}
      pid={pid}
      drawerOpen={drawerOpen}
      onChangeDrawerOpen={onChangeDrawerOpen}
      drawerDisable={drawerDisable}
      listNotices={listNotices}
      total={total}
      getMarkAllAsRead={getMarkAllAsRead}
      getChangeReadStatus={getChangeReadStatus}
      getListNotices={getListNotices}
      isSuper={isSuper}
      showOomNoticeType={showOomNoticeType}
    />
  </div>)
}

NoticeButton.propTypes = {
  reduxState: PropTypes.object,
  appId: PropTypes.any,
  pid: PropTypes.any,
};
const mapStateToProps = state => ({
  reduxState: state,
});

export default connect(mapStateToProps)(NoticeButton);
